import { useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { uniq } from 'lodash';

import getProductImageAngle from 'commons/getProductImageAngle';
import contructHeroImageForNonConfigurableProduct from 'commons/contructHeroImageForNonConfigurableProduct';
import { IMAGE_FLIP_ORIENTATION_VALUES } from 'commons/constants';
import usePdpLastHoveredOptionsExtended from 'components/consumer/PDP/state/pdpLastHoveredOptionsExtended/usePdpLastHoveredOptionsExtended';
import usePdpLastHoveredVariantSku from 'components/consumer/PDP/state/pdpLastHoveredVariantSku';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from 'components/consumer/PDP/state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useScreen from '../../hooks/useScreen';

import {
  getOptionFromOptionName,
  getOptionValueFromValueName,
  getConfigPhotosForSelectedOptions,
} from './utils';

const usePDPPhotos = () => {
  const { isDesktop } = useScreen();

  const {
    loading: basicDetailsLoading,
    error: basicDetailsError,
    data: basicDetails = {},
  } = usePDPBasicDetails();

  const {
    imgsloading: optionLoading, // we need to revisit this when we clear up all the exhaustive dep ignored cases
    error: optionError,
    data: optionData = {},
  } = usePDPOptions();

  const [isVideoSelected, setIsVideoSelected] = useState(false);
  const { search } = useLocation();

  const fabric = useMemo(
    () =>
      new URLSearchParams(search)
        ?.get('fabric')
        ?.replace('-', '_')
        ?.toLowerCase() ?? '',
    [search]
  );

  const {
    hasOrientation,
    options,
    pdpHasUserSelectedOptions,
    selectedOptions,
    selectedOptionsExtended,
    upholsteryOptionId,
  } = optionData;

  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();

  const {
    defaultAngle: productDefaultAngle,
    hero_images: heroImages,
    id: productId,
    name: productName,
    type: productType,
    variation_photos_count: productVariationPhotosCount,
  } = basicDetails ?? {};

  const basicDetailsForImageAngle = useMemo(
    () => ({
      defaultAngle: productDefaultAngle,
      type: productType,
    }),
    [productDefaultAngle, productType]
  );

  const studioPhotos = useMemo(() => {
    if (!heroImages || !options) {
      return [];
    }

    const productAngle = getProductImageAngle({
      product: basicDetailsForImageAngle,
    });

    const allImages = heroImages?.length
      ? heroImages
          .filter(hero => hero?.img?.url)
          .map(
            ({
              img: { url, cropData = {} },
              upholstery_name,
              upholstery_type,
            }) => {
              const option = getOptionFromOptionName(options, upholstery_type);
              const value = getOptionValueFromValueName(
                option,
                upholstery_name
              );

              return {
                id: url,
                src: url,
                type: 'studio',
                cropData,
                upholsteryName: upholstery_name,
                upholsteryType: upholstery_type,
                optionId: option?.id,
                valueId: value?.id,
              };
            }
          )
      : [
          {
            id: 1,
            src: contructHeroImageForNonConfigurableProduct(
              productName,
              productId,
              productAngle
            ).transparent_hero_image_ts,
            type: 'studio',
            cropData: null,
            upholsteryName: null,
            upholsteryType: null,
            optionId: null,
            valueId: null,
          },
        ];

    // If the fabric is specified in a query param, show images with that
    // fabric first
    const imagesOfFabricFromUrl = fabric
      ? allImages.filter(
          photo =>
            photo?.upholsteryName?.toLowerCase().replace(' ', '_') === fabric
        )
      : [];

    const selectedUpholsteryValueId =
      pdpHasUserSelectedOptions && selectedOptions && upholsteryOptionId
        ? selectedOptions[upholsteryOptionId]
        : null;

    // If user has selected an upholstery, show images with that upholstery
    // first in the studio gallery
    const currentSelectionImages = upholsteryOptionId
      ? allImages.filter(
          ({ optionId, valueId }) =>
            `${upholsteryOptionId}` === `${optionId}` &&
            `${selectedUpholsteryValueId}` === `${valueId}`
        )
      : [];

    // Merge image arrays and remove duplicates
    return uniq([
      ...currentSelectionImages,
      ...imagesOfFabricFromUrl,
      ...allImages,
    ]);
  }, [
    basicDetailsForImageAngle,
    fabric,
    heroImages,
    options,
    pdpHasUserSelectedOptions,
    productId,
    productName,
    selectedOptions,
    upholsteryOptionId,
  ]);

  const customProperties = basicDetails?.custom_properties;

  const dimensionalImageDesktop = customProperties?.dimensional_image_desktop;
  const dimensionalImageMobile = customProperties?.dimensional_image_mobile;

  const introVideoMobile =
    customProperties?.video_1x1 && customProperties?.video_1x1 !== ''
      ? customProperties?.video_1x1
      : customProperties?.intro_video_1x1;

  const introVideoDesktop =
    customProperties?.video_3x2 && customProperties?.video_3x2 !== ''
      ? customProperties?.video_3x2
      : customProperties?.intro_video_16x9;

  const dimensionalImage = useMemo(() => {
    if (!customProperties) {
      return null;
    }

    const image = isDesktop ? dimensionalImageDesktop : dimensionalImageMobile;

    return image
      ? {
          id: 1,
          src: image,
          type: 'dimensional',
        }
      : null;
  }, [
    customProperties,
    dimensionalImageDesktop,
    dimensionalImageMobile,
    isDesktop,
  ]);

  const introVideoIds = useMemo(() => {
    if (!customProperties) {
      return null;
    }

    if (introVideoDesktop && introVideoMobile) {
      return {
        desktop: introVideoDesktop,
        mobile: introVideoMobile,
      };
    }

    if (introVideoDesktop && !introVideoMobile) {
      return {
        desktop: introVideoDesktop,
        mobile: introVideoDesktop,
      };
    }

    if (!introVideoDesktop && introVideoMobile) {
      return {
        desktop: introVideoMobile,
        mobile: introVideoMobile,
      };
    }

    return null;
  }, [customProperties, introVideoDesktop, introVideoMobile]);

  // Create a stable identity for a simplified basicDetails object to avoid
  // unnecessary re-renders
  const basicDetailsForImageUtils = useMemo(
    () => ({
      id: productId,
      name: productName,
      type: productType,
      variation_photos_count: productVariationPhotosCount,
    }),
    [productType, productVariationPhotosCount, productId, productName]
  );

  const configurationPhotos = useMemo(() => {
    if (!selectedOptions || !productId) {
      return [];
    }

    const updatedConfigurationPhotos = getConfigPhotosForSelectedOptions(
      selectedOptionsVariantSku,
      selectedOptionsExtended,
      upholsteryOptionId,
      basicDetailsForImageUtils
    );

    return updatedConfigurationPhotos;
  }, [
    basicDetailsForImageUtils,
    productId,
    selectedOptions,
    selectedOptionsExtended,
    selectedOptionsVariantSku,
    upholsteryOptionId,
  ]);

  const isConfigFlipped = useMemo(() => {
    if (
      !selectedOptions ||
      !productId ||
      (!selectedOptionsVariantSku && configurationPhotos.length)
    ) {
      return false;
    }

    const {
      optionId: orientationOptionId,
      defaultValueId: orientationDefaultValueId,
    } = hasOrientation || {};

    if (orientationOptionId && orientationDefaultValueId) {
      const selectedOptionValue = selectedOptionsExtended[
        orientationOptionId
      ]?.value?.value
        .toLowerCase()
        .split(' ')
        .join('_');
      const isImageFlipped = IMAGE_FLIP_ORIENTATION_VALUES.includes(
        selectedOptionValue
      );
      // Reverse orientation for  Orientation Sleeper, Orientation Single Arm Chaise, Chaise Orientation

      return isImageFlipped;
    }

    return false;
  }, [
    configurationPhotos.length,
    hasOrientation,
    productId,
    selectedOptions,
    selectedOptionsExtended,
    selectedOptionsVariantSku,
  ]);

  // Selected photos are an object with the shape { type, id, src } but the
  // src property is optional. Some types such as 'dimensional' never have a src
  // property, but if it is undefined for a configuration or studio photo we can
  // show the first photo in that gallery instead.
  const [userSelectedPhoto, setUserSelectedPhoto] = useState(null);

  const selectPhoto = useCallback(photoOrCallback => {
    setUserSelectedPhoto(photoOrCallback);
    setIsVideoSelected(false);
  }, []);

  const selectVideo = useCallback(() => {
    setUserSelectedPhoto(null);
    setIsVideoSelected(true);
  }, []);

  const pdpLastHoveredOptionsExtended = usePdpLastHoveredOptionsExtended();
  const pdpLastHoveredVariantSku = usePdpLastHoveredVariantSku();

  const lastHoveredConfigPhotos = useMemo(() => {
    if (pdpLastHoveredVariantSku) {
      return [];
    }

    const updatedLastHoveredConfigPhotos = getConfigPhotosForSelectedOptions(
      pdpLastHoveredVariantSku,
      pdpLastHoveredOptionsExtended,
      upholsteryOptionId,
      basicDetailsForImageUtils
    );

    return updatedLastHoveredConfigPhotos;
  }, [
    basicDetailsForImageUtils,
    pdpLastHoveredOptionsExtended,
    pdpLastHoveredVariantSku,
    upholsteryOptionId,
  ]);

  const selectedPhoto = useMemo(() => {
    const defaultImage = configurationPhotos[0] ?? studioPhotos[0];
    const selectedPhotoType = userSelectedPhoto?.type;

    if (selectedPhotoType === 'cylindo') {
      return userSelectedPhoto;
    }

    const selectedPhotoSrc = userSelectedPhoto?.src;

    if (selectedPhotoType === 'configuration' && configurationPhotos.length) {
      return (
        configurationPhotos.find(
          photo => selectedPhotoSrc && photo?.src === selectedPhotoSrc
        ) ??
        configurationPhotos[0] ??
        defaultImage
      );
    }

    if (selectedPhotoType === 'studio' && studioPhotos.length) {
      return (
        studioPhotos.find(
          photo => selectedPhotoSrc && photo?.src === selectedPhotoSrc
        ) ??
        studioPhotos[0] ??
        defaultImage
      );
    }

    if (selectedPhotoType === 'dimensional') {
      return userSelectedPhoto;
    }

    return defaultImage;
  }, [userSelectedPhoto, studioPhotos, configurationPhotos]);

  return {
    loading: basicDetailsLoading || optionLoading,
    error: basicDetailsError || optionError,
    data: {
      campaign: basicDetails?.custom_properties?.campaign,
      configurationPhotos,
      dimensionalImage,
      enableCrops: true,
      introVideoIds,
      isConfigFlipped,
      isVideoSelected,
      lastHoveredConfigPhotos,
      productName,
      selectedPhoto,
      studioPhotos,
    },
    actions: {
      setSelectedPhoto: selectPhoto,
      setIsVideoSelected: selectVideo,
    },
  };
};

export default usePDPPhotos;
